<template>
  <div class="mx-n3">
    <transition name="grow">
      <div v-if="showProgress && (gen.active || gen.done > 0)">
        <div class="frosted p-3">
          {{ gen.active ? 'Generating thumbnails\u2026' : 'Thumbnails generated' }}
          {{ gen.done }}/{{ gen.total }}
          <span v-if="gen.errors.length" class="text-warning ml-2">
            ({{ gen.errors.length }} failed)
          </span>
          <div class="d-flex align-items-center gap-3">
            <b-progress :animated="gen.active" :value="gen.done" :max="gen.total" class="flex-1"/>
            <b-button
              :disabled="!gen.active || gen.cancelled"
              size="sm"
              variant="outline-secondary"
              class="ml-2 py-0"
              @click="cancel"
            >Cancel</b-button>
          </div>
        </div>
      </div>
    </transition>

    <div
      v-if="currentProps"
      style="position:fixed;left:-9999px;width:200px;height:200px;visibility:hidden;pointer-events:none"
      aria-hidden="true"
    >
      <FrameRenderer
        v-if="currentProps"
        ref="renderer"
        v-bind="currentProps"
        :interactive="false"
        :wall-enabled="false"
        :decor-enabled="false"
        :awaiting-config="false"
      />
    </div>
  </div>
</template>

<script>
import FrameRenderer from '@/components/FrameRenderer.vue'
import { http2 } from '@/services/http.js'
import { buildRendererPropsFromItem } from '@/utils/dsItemRendererProps.js'
import { stableSceneStringify } from '@/utils/designStudioSceneSession.js'

export default {
  name: 'ProjectThumbnailGenerator',
  components: { FrameRenderer },
  props: {
    showProgress: { type: Boolean, default: true },
  },

  data() {
    return {
      gen: { active: false, blocking: false, total: 0, done: 0, errors: [], cancelled: false },
      currentProps: null,
    }
  },

  computed: {
    active() { return this.gen.active },
    blocking() { return this.gen.active && this.gen.blocking },
  },

  mounted() {
    this.prepared = new Map()
    this._onBeforeUnload = (e) => {
      if (this.blocking) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._onBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this._onBeforeUnload)
    this.gen.cancelled = true  // stop the loop without showing a toast
    this._destroyed = true
  },

  methods: {
    cancel() {
      this.gen.cancelled = true
    },

    cacheKey(item) {
      if (!item) return ''
      if (item.id != null) return `id:${item.id}`
      return `new:${item.item_sort ?? ''}:${item.imagedata?.id ?? item.imagedata?.local_id ?? ''}`
    },

    renderHash(item) {
      if (!item) return ''
      return stableSceneStringify({ imagedata: item.imagedata || null, width: item.width ?? null, height: item.height ?? null, config_data: item.config_data || {} })
    },

    shouldPrepare(item, committedItems = []) {
      if (!item?.imagedata) return false
      if (item.id == null) return true
      const committed = committedItems.find(committedItem => String(committedItem.id) === String(item.id))
      if (!committed) return true
      return !item.thumbnail || this.renderHash(item) !== this.renderHash(committed)
    },

    preparedThumbnailFor(item) {
      const key = this.cacheKey(item)
      const cached = key ? this.prepared.get(key) : null
      return cached?.hash === this.renderHash(item) ? cached.thumbnail || null : null
    },

    async prepare(items, { committedItems = [] } = {}) {
      const pending = (items || [])
        .filter(item => item && !item.deleted_at && this.shouldPrepare(item, committedItems))
        .map(item => ({
          item,
          key: this.cacheKey(item),
          hash: this.renderHash(item),
        }))
        .filter(entry => {
          if (!entry.key || !entry.hash) return false
          const cached = this.prepared.get(entry.key)
          return !cached || cached.hash !== entry.hash || !cached.thumbnail
        })

      if (!pending.length) return []
      if (this.active) {
        await this._activeRun
      }

      const results = await this.generate(pending.map(entry => entry.item), { persist: false }) || []
      results.forEach((result) => {
        const entry = pending[result?.sourceIndex]
        if (!entry || !result?.thumbnail) return
        this.prepared.set(entry.key, {
          hash: entry.hash,
          thumbnail: result.thumbnail,
        })
      })
      return results
    },

    async start(items, options = {}) {
      return this.generate(items, { persist: true, ...options })
    },

    async generate(items, { persist = false } = {}) {
      if (!items?.length) return
      if (this.gen.active) {
        return this._activeRun || []
      }
      this._activeRun = this._generate(items, { persist })
      try {
        return await this._activeRun
      } finally {
        this._activeRun = null
      }
    },

    async _generate(items, { persist = false } = {}) {
      this.gen = { active: true, blocking: persist, total: items.length, done: 0, errors: [], cancelled: false }
      this._destroyed = false
      const results = []

      for (const [sourceIndex, item] of items.entries()) {
        if (this.gen.cancelled) break

        this.currentProps = buildRendererPropsFromItem(item)
        await this.$nextTick()

        try {
          const dataUrl = await this.$refs.renderer?.generateThumbnail(800)
          if (dataUrl) {
            if (persist) {
              const res = await http2.patch(`/api/v3/design-studio/items/${item.id}/`, { thumbnail: dataUrl })
              const savedUrl = res.data?.thumbnail
              if (savedUrl) {
                results.push({ id: item.id, thumbnail: savedUrl, sourceIndex })
                // Let the parent update its local item via an event
                this.$emit('thumbnail-saved', { id: item.id, thumbnail: savedUrl })
              }
            } else {
              results.push({ id: item.id, thumbnail: dataUrl, sourceIndex })
            }
          }
        } catch (err) {
          this.gen.errors.push({ id: item.id, error: err?.message })
        }

        this.gen.done++
      }

      // Keep the hidden renderer alive between batches. Reusing its scene and
      // WebGL context avoids allocating one THREE.Scene per staged edit; the
      // component's existing beforeDestroy cleanup still runs with the page.
      this.gen.active = false

      if (this._destroyed) return results

      setTimeout(() => { this.gen.done = 0 }, 4000)
      return results
    },
  },
}
</script>

<style scoped lang="scss">
  .grow-enter-active,
  .grow-leave-active {
    overflow: hidden;
    transition: all .4s ease;
    max-height: 80px;
  }
  .grow-enter, .grow-leave-to {
    max-height: 0;
  }
</style>
